import React from "react";
import { AlertCircle, FileText, Maximize2, Minimize2 } from "lucide-react";
import { Button, Spinner } from "@porchpass/ui";
import { useWorkspace } from "./WorkspaceContext";

export function PdfPreviewPanel() {
  const {
    pdfBlobUrl,
    pdfState,
    pdfError,
    actionState,
    isPreviewMaximized,
    handleGenerate,
    refreshPdf,
    togglePreviewMaximized,
  } = useWorkspace();
  const isGenerating = actionState === "generating";

  return (
    <aside className="w-full min-w-0 flex-1 bg-[#1F2937] border-l border-[#E4E7EC] flex flex-col h-full">
      <div className="flex items-center justify-between h-11 px-3 bg-[#101827] text-white text-sm">
        <span>Pre-Approval Letter</span>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="icon"
            onClick={togglePreviewMaximized}
            aria-label={
              isPreviewMaximized
                ? "Restore split view"
                : "Maximize preview panel"
            }
            className="h-7 w-7 border-0 bg-transparent text-[#D1D5DB] hover:bg-[#1F2937] hover:text-white"
          >
            {isPreviewMaximized ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </Button>
          {pdfState === "ready" && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#1453F5] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
              <span className="h-1.5 w-1.5 rounded-full bg-white" />
              Ready
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-1 min-h-0 p-4">
        <div className="flex-1 flex items-center justify-center bg-[#111827] rounded-lg overflow-hidden relative">
          {isGenerating && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
              <div className="flex flex-col items-center gap-2 text-white">
                <Spinner />
                <span className="text-sm">Generating letter...</span>
              </div>
            </div>
          )}
          {pdfState === "ready" && pdfBlobUrl ? (
            <iframe
              title="Pre-approval letter preview"
              src={pdfBlobUrl}
              className="w-full h-full bg-white"
            />
          ) : pdfState === "loading" ? (
            <Spinner className="text-white" />
          ) : pdfState === "missing" ? (
            <div className="flex max-w-xs flex-col items-center gap-3 px-6 text-center text-white">
              <FileText className="h-8 w-8 text-[#9AA2B2]" />
              <div>
                <p className="font-medium">No document generated</p>
                <p className="mt-1 text-sm text-[#9AA2B2]">
                  Generate a pre-approval letter to preview and send it.
                </p>
              </div>
              <Button
                size="s"
                onClick={() => {
                  void handleGenerate().catch(() => undefined);
                }}
              >
                Generate letter
              </Button>
            </div>
          ) : (
            <div className="flex max-w-xs flex-col items-center gap-3 px-6 text-center text-white">
              <AlertCircle className="h-8 w-8 text-[#FCA5A5]" />
              <div>
                <p className="font-medium">Preview unavailable</p>
                <p className="mt-1 text-sm text-[#D1D5DB]">
                  {pdfError ?? "Unable to load the document."}
                </p>
              </div>
              <Button
                variant="secondary"
                size="s"
                onClick={() => {
                  void refreshPdf().catch(() => undefined);
                }}
              >
                Try again
              </Button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
